/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 27/01/22
 */

import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { User } from '@/wpt-lib/interfaces/User';
import { withDisplayName } from '@/utils/withDisplayName';
import { Flex, GridItem } from '@chakra-ui/react';
import React, { ComponentType } from 'react';

export interface WithCurrentAssignmentProps {
    currentAssignment: Assignment;
    user?: User;
}

export const withCurrentAssignment = <
    T extends WithCurrentAssignmentProps,
    R = Omit<T, keyof WithCurrentAssignmentProps>,
>(
    WrappedComponent: ComponentType<T>,
): ComponentType<R> =>
    withDisplayName(
        ((props: T) => {
            const { currentAssignment, loading, error, user } = useAssignment();

            return (
                <StateDisplay
                    data={currentAssignment}
                    loading={loading}
                    error={error}
                    errorRender={() => (
                        <GridItem colSpan={2} as={Flex} justifyContent="center">
                            <SimpleAlert variant="top-accent">
                                Could not load user data. Please refresh the page to try again.
                            </SimpleAlert>
                        </GridItem>
                    )}
                    render={(assignment) => {
                        return <WrappedComponent {...props} currentAssignment={assignment} user={user} />;
                    }}
                />
            );
        }) as unknown as ComponentType<R>,
        'withCurrentAssignment',
    );
